import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AdminPageHeader } from '../components/layout/AdminPageHeader';
import { Card, Badge, Button, Spinner } from '../components/ui';
import { useTranslation } from 'react-i18next';
import { adminApi } from '../services/api';
import { toast } from 'sonner';

const AdminPayments = () => {
  const { t } = useTranslation();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  useEffect(() => {
    fetchPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const data = await adminApi.getTransactions();
      setTransactions(data || []);
    } catch (err) {
      toast.error(t('adminPayments.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const filteredTransactions = transactions.filter(tx => 
    (tx.user?.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (tx.type || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const paginatedTransactions = filteredTransactions.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);
  const totalPages = Math.ceil(filteredTransactions.length / ITEMS_PER_PAGE);

  if (loading) return <div className="h-[60vh] flex items-center justify-center"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-10 pb-10">
      <AdminPageHeader
        tag="FINANCIALS"
        title={t('dashboard.paymentManagement') || 'Payment Management'}
        subtitle={`${transactions.length} ${t('dashboard.totalTransactions') || 'TRANSACTIONS PROCESSED'}`}
        icon="payments"
      >
        <div className="relative group flex-1">
          <input
            type="text"
            placeholder={t('dashboard.searchPayments') || 'Search transactions...'}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(1);
            }}
            className="w-full sm:w-80 h-14 bg-white dark:bg-slate-900 border-2 border-slate-100 dark:border-slate-800 rounded-3xl px-12 outline-none focus:border-[#1e9f6b]/50 transition-all font-bold text-sm dark:text-white shadow-xl shadow-slate-900/5"
          />
          <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#1e9f6b] transition-colors">
            search
          </span>
        </div>
        <Button
          variant="ghost"
          onClick={fetchPayments}
          className="h-14 w-14 rounded-3xl p-0 hover:bg-[#1e9f6b]/10 hover:text-[#1e9f6b] transition-colors"
        >
          <span className="material-symbols-outlined">refresh</span>
        </Button>
      </AdminPageHeader>

      {/* Transactions Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <Card className="overflow-hidden bg-white dark:bg-slate-900 rounded-[40px] border-slate-100 dark:border-slate-800/50 shadow-2xl shadow-slate-900/5">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-separate border-spacing-0">
              <thead>
                <tr className="bg-slate-50/50 dark:bg-slate-900/30">
                  <th className="px-10 py-6 text-[9px] font-black uppercase text-slate-400 italic tracking-[0.2em]">{t('dashboard.date')}</th>
                  <th className="px-6 py-6 text-[9px] font-black uppercase text-slate-400 italic tracking-[0.2em]">{t('dashboard.user')}</th>
                  <th className="px-6 py-6 text-[9px] font-black uppercase text-slate-400 italic tracking-[0.2em]">{t('dashboard.amount')}</th>
                  <th className="px-6 py-6 text-[9px] font-black uppercase text-slate-400 italic tracking-[0.2em]">{t('dashboard.type')}</th>
                  <th className="px-6 py-6 text-[9px] font-black uppercase text-slate-400 italic tracking-[0.2em]">{t('dashboard.reason')}</th>
                  <th className="px-10 py-6 text-[9px] font-black uppercase text-slate-400 italic tracking-[0.2em] text-right">{t('dashboard.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50 dark:divide-slate-800/50">
                <AnimatePresence mode="popLayout">
                  {paginatedTransactions.map((tx, idx) => (
                    <motion.tr 
                      key={tx.id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 10 }}
                      transition={{ delay: idx * 0.05 }}
                      className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-all duration-300 group"
                    >
                      <td className="px-10 py-6">
                        <div className="text-[10px] font-black text-slate-400 uppercase italic tracking-widest">{new Date(tx.createdAt).toLocaleString()}</div>
                      </td>

                      <td className="px-6 py-6">
                        <div className="flex items-center gap-4">
                          <div className="h-10 w-10 bg-slate-100 dark:bg-slate-800 rounded-xl flex items-center justify-center overflow-hidden transition-all duration-500 italic uppercase shadow-inner">
                            {tx.user?.avatarUrl ? (
                              <img src={tx.user.avatarUrl} alt="" className="h-full w-full object-cover" />
                            ) : (
                              <span className="font-black text-xs text-slate-400">{tx.user?.name?.[0] || 'U'}</span>
                            )}
                          </div>
                          <div>
                            <div className="font-black text-xs italic uppercase tracking-tight text-slate-900 dark:text-white">{tx.user?.name || t('operatorDashboard.customer')}</div>
                            <div className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">{tx.user?.phone}</div>
                          </div>
                        </div>
                      </td>

                      <td className="px-6 py-6">
                        <span className={`text-xs font-black italic uppercase tracking-tighter ${tx.amount > 0 ? 'text-[#1e9f6b]' : 'text-rose-500'}`}>
                           {tx.amount > 0 ? '+' : ''}{(tx.amount || 0).toLocaleString()} {t('common.uzs')}
                        </span>
                      </td>

                      <td className="px-6 py-6">
                        <Badge variant={tx.type === 'TOPUP' ? 'green' : tx.type === 'PAYMENT' ? 'blue' : 'slate'} className="rounded-xl font-black italic uppercase text-[8px] tracking-wider px-3 py-1.5 border-0">
                          {tx.type}
                        </Badge>
                      </td>

                      <td className="px-6 py-6">
                         <span className="text-[10px] font-bold text-slate-400 uppercase tracking-tight line-clamp-1">{tx.reason || '—'}</span>
                      </td>

                      <td className="px-10 py-6 text-right">
                         <Button size="xs" variant="ghost" className="h-10 w-10 rounded-xl p-0 hover:bg-slate-100 dark:hover:bg-slate-800">
                            <span className="material-symbols-outlined text-[20px]">receipt_long</span>
                         </Button>
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-10 py-8 border-t border-slate-50 dark:border-slate-800/50 flex justify-between items-center bg-slate-50/10 dark:bg-slate-800/5">
              <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest italic">
                {t('common.pageOf', { page, total: totalPages })}
              </div>
              <div className="flex gap-3">
                <Button size="sm" variant="ghost" disabled={page === 1} onClick={() => setPage(p => p - 1)} className="rounded-2xl h-12 px-6 font-black uppercase italic text-[10px] tracking-widest bg-white dark:bg-slate-800 shadow-sm">
                  <span className="material-symbols-outlined text-sm mr-2">arrow_back</span>
                  {t('common.prev')}
                </Button>
                <Button size="sm" variant="ghost" disabled={page === totalPages} onClick={() => setPage(p => p + 1)} className="rounded-2xl h-12 px-6 font-black uppercase italic text-[10px] tracking-widest bg-white dark:bg-slate-800 shadow-sm">
                  {t('common.next')}
                  <span className="material-symbols-outlined text-sm ml-2">arrow_forward</span>
                </Button>
              </div>
            </div>
          )}
        </Card>
      </motion.div>
    </div>
  );
};

export default AdminPayments;